// Complete, bounded, read-only loading through the existing single-page API.
// CommonJS also lets the backend's synthetic tests exercise the real loader.
const EFFECTIVE_LEADS_PAGE_SIZE = 2000;

function canonicalEffectiveLeadsScope(options = {}) {
  const countyIds = [...new Set((options.countyIds || []).map(Number))].sort((a, b) => a - b);
  if (countyIds.some((id) => !Number.isSafeInteger(id) || id <= 0)) throw new Error('Select valid counties before loading leads.');
  const filterMode = ['atleast', 'atmax', 'between'].includes(options.filterMode) ? options.filterMode : 'between';
  const number = (value) => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) throw new Error('Use valid acreage filters before loading leads.');
    return parsed;
  };
  return { countyIds, filterMode,
    minAcres: filterMode === 'atmax' ? null : (number(options.minAcres) || null),
    maxAcres: filterMode === 'atleast' ? null : number(options.maxAcres),
  };
}

const effectiveLeadsScopeSignature = (options) => JSON.stringify(canonicalEffectiveLeadsScope(options));
function abortError() {
  const error = new Error('Loading cancelled; results are incomplete.');
  error.name = 'AbortError';
  return error;
}
function checkAborted(signal) { if (signal?.aborted) throw abortError(); }
function inconsistent() { throw new Error('Effective Leads changed or returned inconsistent pages. Results are incomplete; reload the scope.'); }
function configSignature(config) {
  if (!config || typeof config.algorithmVersion !== 'string' || !config.algorithmVersion
    || !Number.isFinite(config.individualThreshold) || !Number.isFinite(config.spouseThreshold)) inconsistent();
  return JSON.stringify([config.algorithmVersion, config.individualThreshold, config.spouseThreshold]);
}

async function loadAllEffectiveLeads(fetchPage, options, { onProgress, signal } = {}) {
  const scope = canonicalEffectiveLeadsScope(options);
  if (!scope.countyIds.length) throw new Error('Select at least one county before loading leads.');
  const rows = [];
  const ids = new Set();
  let total = null;
  let firstConfig;
  let firstConfigSignature;
  let firstRevision;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort);
  let loaded = 0;
  const request = async (offset, limit) => {
    checkAborted(signal);
    checkAborted(controller.signal);
    // A fresh scope copy for each request cannot be mutated by a caller or
    // adapter while a later page is pending. No automatic transport retries.
    const response = await fetchPage({ ...scope, countyIds: [...scope.countyIds], limit, offset }, { signal: controller.signal });
    checkAborted(signal);
    checkAborted(controller.signal);
    const page = response?.pagination;
    if (response?.ok === false || !Array.isArray(response?.rows) || !page
      || !Number.isSafeInteger(page.total) || page.total < 0
      || !Number.isSafeInteger(page.limit) || page.limit < 1 || page.limit > EFFECTIVE_LEADS_PAGE_SIZE
      || !Number.isSafeInteger(page.offset) || page.offset !== offset
      || typeof page.hasMore !== 'boolean' || response.rows.length > page.limit || response.rows.length > limit
      || !Array.isArray(response.countyIds)
      || JSON.stringify([...new Set(response.countyIds.map(Number))].sort((a, b) => a - b)) !== JSON.stringify(scope.countyIds)) inconsistent();
    const currentConfigSignature = configSignature(response.config);
    if (total === null) {
      total = page.total;
      firstConfig = { ...response.config };
      firstConfigSignature = currentConfigSignature;
      firstRevision = response.scopeRevision;
    } else if (page.total !== total || currentConfigSignature !== firstConfigSignature || response.scopeRevision !== firstRevision) inconsistent();
    const nextOffset = page.offset + response.rows.length;
    if (nextOffset > total || page.hasMore !== (nextOffset < total)
      || (page.hasMore && nextOffset <= offset)) inconsistent();
    for (const row of response.rows) {
      if (!row || typeof row.lead_id !== 'string' || !row.lead_id.trim() || ids.has(row.lead_id)) inconsistent();
      ids.add(row.lead_id);
    }
    loaded += response.rows.length;
    onProgress?.({ loaded, total });
    checkAborted(signal);
    return response;
  };
  try {
    const first = await request(0, EFFECTIVE_LEADS_PAGE_SIZE);
    rows.push(...first.rows);
    const width = first.pagination.limit;
    // Disjoint offset windows preserve order even when responses finish out of
    // order. Fill a short page inside its own window before moving on.
    const readWindow = async (start, end) => {
      const result = [];
      while (start + result.length < end) {
        const page = await request(start + result.length, end - start - result.length);
        if (!page.rows.length) inconsistent();
        result.push(...page.rows);
      }
      return result;
    };
    const expectedTotal = total;
    for (let start = rows.length; start < expectedTotal; start += width * 2) {
      const windows = [start, start + width].filter((offset) => offset < expectedTotal);
      const results = await Promise.allSettled(windows.map(async (offset) => {
        try { return await readWindow(offset, Math.min(offset + width, expectedTotal)); }
        catch (error) { controller.abort(); throw error; }
      }));
      const failed = results.find((result) => result.status === 'rejected');
      if (failed) throw failed.reason;
      for (const result of results) rows.push(...result.value);
    }
    checkAborted(signal);
    if (rows.length !== total || ids.size !== total) inconsistent();
    return { rows, countyIds: [...scope.countyIds], config: firstConfig,
      pagination: { total, offset: 0, limit: width, hasMore: false } };
  } finally {
    controller.abort();
    signal?.removeEventListener('abort', abort);
  }
}

module.exports = { EFFECTIVE_LEADS_PAGE_SIZE, canonicalEffectiveLeadsScope, effectiveLeadsScopeSignature, loadAllEffectiveLeads };
